// @flow
import React, { Component } from 'react';
import AnnotationRep from 'app/common/react/components/HomefeedCurator/AnnotationRep';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { Experience } from 'app/common/lib/react/experiences/experienceUtils';
import userHomefeedResourceOptions from 'app/common/react/components/FeedsAndFollowing/Homefeed/userHomefeedResourceOptions';
import withExperienceClient from 'app/common/lib/react/experiences/withExperienceClient';
import withResource from 'app/common/lib/react/withResource';
import { resourceFetch } from 'app/common/redux/actions/resourceFetch';
import { track } from 'app/common/lib/react/analytics';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import { Box, Button, Spinner, Text } from 'gestalt';
import { compose, type Dispatch } from 'redux';

const disabledStyle = {
  pointerEvents: 'none',
  opacity: 0.5,
  transition: 'opacity 0.2s',
};

type Annotation = {
  id: string,
  name: string,
  large_thumb_image_url: string,
  background_color:
    | 'watermelon'
    | 'orange'
    | 'green'
    | 'pine'
    | 'olive'
    | 'midnight'
    | 'navy'
    | 'blue'
    | 'purple'
    | 'orchid'
    | 'eggplant'
    | 'maroon',
};

type Curator = {
  data: ?{
    annotations: Array<Annotation>,
    blacklist: Array<Annotation>,
  },
  isFetching: boolean,
  isLoaded: boolean,
};

type State = {
  // a map of annotations ids to their selection state
  annotationSelectionMap: { [string]: boolean },
  // a map of blacklisted ids to their selection state
  blacklistSelectionMap: { [string]: boolean },
  isSaving: boolean,
};

type Props = {
  curator: Curator,
  dispatch: Dispatch<any>,
  experience: Experience,
  handleCompleteExperience: Function,
  onDismiss: Function,
  viewer: Viewer,
};

class CuratorModal extends Component<Props, State> {
  // This component shows a modal with a list of images representing the annotations the user have interacted.
  // The user can select and unselect any of the annotations which will,
  // in turn, modify their homefeed accroding to the selection.
  state = { annotationSelectionMap: {}, blacklistSelectionMap: {}, isSaving: false };

  getSelectedAnnotationId(selectionMap: { [string]: boolean }) {
    return Object.keys(selectionMap).filter((id) => selectionMap[id]);
  }

  logSelection = (elementType: string, objectId: string) => {
    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.HOMEFEED_CURATOR,
      element: elementType,
      objectId,
    });
  };

  handleClickSelectAnnotation = (annotationId: string) => {
    this.setState((prevState) => {
      const newSelectionState = !prevState.annotationSelectionMap[annotationId];
      const newAnnotationSelectionMap = {
        ...prevState.annotationSelectionMap,
        [annotationId]: newSelectionState,
      };

      if (newSelectionState) {
        this.logSelection(
          PCONST.thrift.ElementType.HOMEFEED_CURATOR_ANNOTATION_SELECT,
          annotationId,
        );
      } else {
        this.logSelection(
          PCONST.thrift.ElementType.HOMEFEED_CURATOR_ANNOTATION_UNSELECT,
          annotationId,
        );
      }

      return { ...prevState, annotationSelectionMap: newAnnotationSelectionMap };
    });
  };

  handleClickSelectBlacklist = (annotationId: string) => {
    this.setState((prevState) => {
      const newSelectionState = !prevState.blacklistSelectionMap[annotationId];
      const newBlacklistSelectionState = {
        ...prevState.blacklistSelectionMap,
        [annotationId]: newSelectionState,
      };

      if (newSelectionState) {
        this.logSelection(
          PCONST.thrift.ElementType.HOMEFEED_CURATOR_BLACKLIST_SELECT,
          annotationId,
        );
      } else {
        this.logSelection(
          PCONST.thrift.ElementType.HOMEFEED_CURATOR_BLACKLIST_UNSELECT,
          annotationId,
        );
      }

      return { ...prevState, blacklistSelectionMap: newBlacklistSelectionState };
    });
  };

  handleDismiss = () => {
    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.HOMEFEED_CURATOR,
      element: PCONST.thrift.ElementType.HOMEFEED_CURATOR_DISMISS_BUTTON,
    });
    this.props.onDismiss();
  };

  handleSave = () => {
    this.setState({ isSaving: true });
    const { experience, dispatch, handleCompleteExperience, onDismiss, viewer } = this.props;
    const { annotationSelectionMap, blacklistSelectionMap } = this.state;

    const newIdsBlacklist = this.getSelectedAnnotationId(annotationSelectionMap);
    const newIdsWhitelist = this.getSelectedAnnotationId(blacklistSelectionMap);

    ResourceFactory.create('CuratorResource', {
      blacklist: newIdsBlacklist,
      whitelist: newIdsWhitelist,
    })
      .callUpdate()
      .then((response) => {
        this.setState({ isSaving: false });
        onDismiss();
      });

    dispatch(
      resourceFetch(
        'UserHomefeedResource',
        userHomefeedResourceOptions({
          experience,
          inNux: false,
          staticFeed: false,
          viewerIsPartner: viewer.isAuth && viewer.isPartner,
        }),
      ),
    );

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.HOMEFEED_CURATOR,
      element: PCONST.thrift.ElementType.HOMEFEED_CURATOR_DONE_BUTTON,
    });

    if (handleCompleteExperience) {
      handleCompleteExperience();
    }
  };

  renderAnnotations(annotations: Array<Annotation>, isBlacklist: boolean) {
    return (
      <Box
        alignItems="start"
        display="flex"
        justifyContent="start"
        marginLeft={-2}
        marginRight={-2}
        overflow="hidden"
        position="relative"
        wrap
      >
        {annotations.map((annotation, i) => (
          <Box key={annotation.name} padding={2} width="33%">
            <AnnotationRep
              backgroundColor={annotation.background_color}
              name={annotation.name}
              id={annotation.id}
              image={annotation.large_thumb_image_url}
              isBlacklist={isBlacklist}
              onClick={
                isBlacklist ? this.handleClickSelectBlacklist : this.handleClickSelectAnnotation
              }
            />
          </Box>
        ))}
      </Box>
    );
  }

  renderCuratorContent() {
    if (
      this.props.curator.isFetching ||
      !this.props.curator.isLoaded ||
      this.props.curator.data == null
    ) {
      return (
        <Spinner
          accessibilityLabel={i18n._('loading ideas', 'Spinnner to show content is loading')}
          show
        />
      );
    } else {
      const { annotations, blacklist } = this.props.curator.data;
      const { isSaving } = this.state;

      return (
        <Box dangerouslySetInlineStyle={{ __style: isSaving ? disabledStyle : {} }}>
          <Box paddingY={4}>
            <Text bold size="xl">
              {i18n._(
                'These are the ideas in your feed',
                'Title to explain the content of the modal',
              )}
            </Text>
            <Text size="xl">
              {i18n._(
                'Pick the ones you want to see less of',
                'Subtitle to let user start selection',
              )}
            </Text>
          </Box>

          {this.renderAnnotations(annotations, false)}

          {blacklist.length ? (
            <Box paddingY={4}>
              <Text bold size="xl">
                {i18n._(
                  'These ideas are hidden from your feed',
                  'Title to explain why users are seeing the ideas below',
                )}
              </Text>
              <Text size="xl">
                {i18n._(
                  'Check the ones you like to put them back in your feed',
                  'Subtitle to inform user that they can add ideas back to their feed',
                )}
              </Text>
            </Box>
          ) : null}

          {this.renderAnnotations(blacklist, true)}
        </Box>
      );
    }
  }

  render() {
    return (
      <Modal
        size="lg"
        footer={
          <Box paddingX={2}>
            <Button
              text={i18n._('Done', 'Button to save selection')}
              size="lg"
              onClick={this.handleSave}
              disabled={this.state.isSaving}
            />
          </Box>
        }
        heading={i18n._(
          'Tune your home feed',
          'Title of the modal which allows user to tune their homefeed',
        )}
        accessibilityModalLabel={i18n._(
          'Tune your homefeed',
          'Label to inform user that this modal is to tune homefeed',
        )}
        accessibilityCloseLabel={i18n._('Dismiss selection', 'Label for dismissing the modal')}
        onDismiss={this.handleDismiss}
      >
        <Box color="white" shape="rounded" paddingX={8}>
          {this.renderCuratorContent()}
        </Box>
      </Modal>
    );
  }
}

export default compose(
  withExperienceClient,
  withResource(
    {
      name: 'CuratorResource',
      key: 'curator',
    },
    { allowStale: false },
  ),
  withViewer,
)(CuratorModal);
